// === META-PROGRESSION ===
// Statistiques persistantes et achievements sauvegardés en localStorage.
// Persiste entre les runs. Une seule instance globale.

const STORAGE_KEY = 'deadtide_meta';

/** Version actuelle du jeu — source de vérité partagée avec BootScene */
export const GAME_VERSION = '0.40.7-beta';

// === STATISTIQUES ===

export interface MetaStats {
  totalRuns: number;
  runsWon: number;
  runsLost: number;
  totalDeaths: number;
  enemiesKilled: number;
  bossesKilled: number;
  totalGoldEarned: number;
  totalGoldSpent: number;
  itemsLooted: number;
  nodesVisited: number;
  eventsCompleted: number;
  tilesExplored: number;
  healingReceived: number;
  damageDealt: number;
  damageTaken: number;
  bestRunKills: number;     // record d'ennemis tués en un run
  bestRunGold: number;      // record d'or accumulé en un run
  floorsReached: number;    // étage max atteint
}

const DEFAULT_STATS: MetaStats = {
  totalRuns: 0,
  runsWon: 0,
  runsLost: 0,
  totalDeaths: 0,
  enemiesKilled: 0,
  bossesKilled: 0,
  totalGoldEarned: 0,
  totalGoldSpent: 0,
  itemsLooted: 0,
  nodesVisited: 0,
  eventsCompleted: 0,
  tilesExplored: 0,
  healingReceived: 0,
  damageDealt: 0,
  damageTaken: 0,
  bestRunKills: 0,
  bestRunGold: 0,
  floorsReached: 0,
};

// === ACHIEVEMENTS ===

export interface Achievement {
  id: string;
  name: string;
  emoji: string;
  description: string;
  /** Condition : reçoit les stats et retourne true si débloqué */
  check: (stats: MetaStats) => boolean;
  /** ID d'item débloqué par cet achievement (optionnel) */
  unlockItemId?: string;
}

export const ACHIEVEMENTS: Achievement[] = [
  // Premiers pas
  {
    id: 'first_blood',
    name: 'Premier Sang',
    emoji: '🩸',
    description: 'Tuer un ennemi',
    check: (s) => s.enemiesKilled >= 1,
  },
  {
    id: 'first_run',
    name: 'Baptême du Feu',
    emoji: '🔥',
    description: 'Terminer un run (victoire ou défaite)',
    check: (s) => s.totalRuns >= 1,
  },
  {
    id: 'first_victory',
    name: 'Victoire !',
    emoji: '🏆',
    description: 'Vaincre le boss et terminer un run',
    check: (s) => s.runsWon >= 1,
  },

  // Combat
  {
    id: 'kill_10',
    name: 'Tueur de Morts',
    emoji: '💀',
    description: 'Tuer 10 ennemis au total',
    check: (s) => s.enemiesKilled >= 10,
  },
  {
    id: 'kill_50',
    name: 'Fléau des Zombies',
    emoji: '☠️',
    description: 'Tuer 50 ennemis au total',
    check: (s) => s.enemiesKilled >= 50,
    unlockItemId: 'sabre_captain',
  },
  {
    id: 'kill_100',
    name: 'Légende des Mers',
    emoji: '⚔️',
    description: 'Tuer 100 ennemis au total',
    check: (s) => s.enemiesKilled >= 100,
    unlockItemId: 'kraken_tentacle',
  },
  {
    id: 'boss_slayer',
    name: 'Tueur de Boss',
    emoji: '👹',
    description: 'Vaincre 3 boss',
    check: (s) => s.bossesKilled >= 3,
    unlockItemId: 'kraken_eye',
  },
  {
    id: 'boss_master',
    name: 'Maître des Boss',
    emoji: '🐙',
    description: 'Vaincre 10 boss',
    check: (s) => s.bossesKilled >= 10,
  },

  // Richesse
  {
    id: 'gold_100',
    name: 'Petit Pactole',
    emoji: '💰',
    description: 'Gagner 100 or au total',
    check: (s) => s.totalGoldEarned >= 100,
  },
  {
    id: 'gold_500',
    name: 'Coffre Plein',
    emoji: '💎',
    description: 'Gagner 500 or au total',
    check: (s) => s.totalGoldEarned >= 500,
    unlockItemId: 'voodoo_sash',
  },
  {
    id: 'gold_run_50',
    name: 'Expédition Dorée',
    emoji: '🏴‍☠️',
    description: 'Accumuler 50+ or en un seul run',
    check: (s) => s.bestRunGold >= 50,
  },

  // Exploration
  {
    id: 'explorer',
    name: 'Explorateur',
    emoji: '🔍',
    description: 'Visiter 20 nœuds au total',
    check: (s) => s.nodesVisited >= 20,
  },
  {
    id: 'cartographer',
    name: 'Cartographe',
    emoji: '🗺️',
    description: 'Visiter 50 nœuds au total',
    check: (s) => s.nodesVisited >= 50,
    unlockItemId: 'sprint_greaves',
  },

  // Endurance
  {
    id: 'survivor_5',
    name: 'Survivant',
    emoji: '🩹',
    description: 'Compléter 5 runs',
    check: (s) => s.totalRuns >= 5,
  },
  {
    id: 'survivor_10',
    name: 'Vétéran',
    emoji: '🎖️',
    description: 'Compléter 10 runs',
    check: (s) => s.totalRuns >= 10,
    unlockItemId: 'ghost_cloak',
  },
  {
    id: 'win_3',
    name: 'Capitaine Confirmé',
    emoji: '🏅',
    description: 'Gagner 3 runs',
    check: (s) => s.runsWon >= 3,
    unlockItemId: 'cursed_ring',
  },

  // Records
  {
    id: 'run_kills_10',
    name: 'Massacre',
    emoji: '🔪',
    description: 'Tuer 10+ ennemis en un seul run',
    check: (s) => s.bestRunKills >= 10,
  },
  {
    id: 'floor_7',
    name: 'Au Sommet',
    emoji: '⛰️',
    description: 'Atteindre l\'étage 7',
    check: (s) => s.floorsReached >= 7,
  },
];

// === MANAGER ===

export class MetaManager {
  private stats: MetaStats;
  private unlockedAchievements: Set<string>;
  // Stats du run courant (pour les records)
  private runKills = 0;
  private runGold = 0;
  // Version sauvegardée dans le localStorage (null = première fois)
  private savedVersion: string | null = null;

  constructor() {
    const saved = this.load();
    this.stats = saved.stats;
    this.unlockedAchievements = new Set(saved.achievements);
  }

  // === STATS ===

  getStats(): MetaStats {
    return { ...this.stats };
  }

  /** Kills du run en cours (pour soumission leaderboard). Reset par startRun()/endRun(). */
  getCurrentRunKills(): number {
    return this.runKills;
  }

  /** Incrémente un stat numérique */
  addStat(key: keyof MetaStats, amount: number = 1): void {
    this.stats[key] += amount;

    // Tracker les stats de run pour les records
    if (key === 'enemiesKilled') this.runKills += amount;
    if (key === 'totalGoldEarned') this.runGold += amount;

    this.checkAchievements();
    this.save();
  }

  /** Met à jour un record (max) */
  updateRecord(key: keyof MetaStats, value: number): void {
    if (value > this.stats[key]) {
      this.stats[key] = value;
      this.checkAchievements();
      this.save();
    }
  }

  /** Appelé en fin de run pour finaliser les records */
  endRun(won: boolean): void {
    this.stats.totalRuns++;
    if (won) this.stats.runsWon++;
    else this.stats.runsLost++;

    this.updateRecord('bestRunKills', this.runKills);
    this.updateRecord('bestRunGold', this.runGold);

    this.runKills = 0;
    this.runGold = 0;

    this.checkAchievements();
    this.save();
  }

  /** Reset les compteurs de run (au début d'un nouveau) */
  startRun(): void {
    this.runKills = 0;
    this.runGold = 0;
  }

  // === ACHIEVEMENTS ===

  getUnlockedAchievements(): string[] {
    return [...this.unlockedAchievements];
  }

  isAchievementUnlocked(id: string): boolean {
    return this.unlockedAchievements.has(id);
  }

  /** Vérifie tous les achievements et retourne les nouveaux débloqués */
  checkAchievements(): string[] {
    const newlyUnlocked: string[] = [];
    for (const ach of ACHIEVEMENTS) {
      if (!this.unlockedAchievements.has(ach.id) && ach.check(this.stats)) {
        this.unlockedAchievements.add(ach.id);
        newlyUnlocked.push(ach.id);
      }
    }
    if (newlyUnlocked.length > 0) this.save();
    return newlyUnlocked;
  }

  /** Retourne les IDs d'items débloqués par les achievements */
  getUnlockedItems(): string[] {
    const items: string[] = [];
    for (const ach of ACHIEVEMENTS) {
      if (ach.unlockItemId && this.unlockedAchievements.has(ach.id)) {
        items.push(ach.unlockItemId);
      }
    }
    return items;
  }

  /** Vérifie si un item est débloqué (pas de verrou OU achievement obtenu) */
  isItemUnlocked(itemId: string): boolean {
    // Vérifier si cet item nécessite un unlock
    const lockingAch = ACHIEVEMENTS.find(a => a.unlockItemId === itemId);
    if (!lockingAch) return true; // pas verrouillé
    return this.unlockedAchievements.has(lockingAch.id);
  }

  // === VERSION ===

  /** Retourne la version du jeu enregistrée dans la sauvegarde (null si aucune sauvegarde) */
  getSavedVersion(): string | null {
    return this.savedVersion;
  }

  /**
   * Retourne true si une sauvegarde existe avec une version différente de la version actuelle.
   * Indique qu'une migration ou un reset peut être nécessaire.
   */
  hasSaveVersionMismatch(): boolean {
    return this.savedVersion !== null && this.savedVersion !== GAME_VERSION;
  }

  // === PERSISTANCE ===

  private save(): void {
    const data = {
      version: GAME_VERSION,
      stats: this.stats,
      achievements: [...this.unlockedAchievements],
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch {
      // localStorage plein ou indisponible — on ignore
    }
  }

  private load(): { stats: MetaStats; achievements: string[] } {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        // Mémoriser la version sauvegardée pour détecter les mises à jour
        this.savedVersion = data.version ?? null;
        // Merge avec les défauts (si nouvelles stats ajoutées après)
        return {
          stats: { ...DEFAULT_STATS, ...data.stats },
          achievements: data.achievements ?? [],
        };
      }
    } catch {
      // données corrompues — reset
    }
    return { stats: { ...DEFAULT_STATS }, achievements: [] };
  }

  /** Reset complet (pour debug) */
  resetAll(): void {
    this.stats = { ...DEFAULT_STATS };
    this.unlockedAchievements.clear();
    this.runKills = 0;
    this.runGold = 0;
    this.save();
  }
}
